/* eslint-disable @typescript-eslint/ban-types */
import React, { useEffect, useMemo, useState } from 'react';

import { DropdownInput } from '../..';
import { defaultFilterHeight, defaultFilterWidth } from '../constants';

interface DropdownInputQueryFilterProps<
    FilterInput extends Record<string, TableStringFilterInput>,
    DataItem extends Record<string, unknown>,
> {
    /**
     * The fields on the TableStringFilterInput to filter by
     */
    fieldNames: (keyof FilterInput)[];
    /**
     * State of a filter input that includes a property for `fieldName` which allows use of a
     * `contains` filter
     */
    filterInput: FilterInput;
    /**
     * A default option which will be listed first in the DropdownInput options list
     */
    defaultOption?: DropdownInputOption;
    /**
     * The HTML id to apply to the input element
     */
    elementId: string;
    /**
     * A function for converting a data object into a GetDropdownInputOption. This is used
     * to map the preFilteredData array into a list of unique DropdownInputOptions.
     */
    getFilterOption: GetDropdownInputOption<DataItem>;
    /**
     * Height for DropdownInput
     */
    height?: number;
    /**
     * Array of data before filtering; used to create the set of available options
     */
    preFilteredData: DataItem[];
    /**
     * Method to set state of a filter input that includes a property for `fieldName` which allows
     * use of a `contains` filter
     */
    setFilterInput: SetState<FilterInput>;
    /**
     * Width for DropdownInput
     */
    width?: number;
}

/**
 * Returns a React component which renders DropdownInput that allows the user to select a single
 * option from a de-duplicated list of values. The selected value is used to set the `contains`
 * property of a filter input.
 *
 * @remarks
 * This filter is implemented as an isolated component (outside of the react-table filtering API)
 * because it is used to set the AppSync query filter input, rather than filtering the data that is
 * already passed to a react-table instance.
 * @param props - {@link DropdownInputQueryFilterProps}
 */
export function DropdownInputQueryFilter<
    FilterInput extends Record<string, TableStringFilterInput>,
    DataItem extends Record<string, unknown>,
>({
    defaultOption,
    elementId,
    fieldNames,
    filterInput,
    height = defaultFilterHeight,
    getFilterOption,
    preFilteredData,
    setFilterInput,
    width = defaultFilterWidth,
}: DropdownInputQueryFilterProps<FilterInput, DataItem>): JSX.Element {
    const options = useMemo(() => {
        // Using a Map here keeps the order of the options consistent with the order of the
        // preFilteredData because Maps remember insertion order
        const optionsMap = preFilteredData.reduce(
            (
                acc: Map<string, DropdownInputOption>,
                curr: DataItem,
            ): Map<string, DropdownInputOption> => {
                const dropdownInputOption = getFilterOption(curr);

                if (dropdownInputOption.id && dropdownInputOption.label) {
                    acc.set(dropdownInputOption.id, dropdownInputOption);
                }

                return acc;
            },
            new Map<string, DropdownInputOption>(),
        );

        const dropdownInputOptions = Array.from(optionsMap.values());

        if (defaultOption === undefined) {
            return dropdownInputOptions;
        }

        return [defaultOption].concat(dropdownInputOptions);
    }, [preFilteredData]);

    const [resetTimestamp, setResetTimestamp] = useState(0);

    // If the filter value is removed, set the DropdownInput to its default value, i.e. all
    // options (no filtering)
    useEffect(() => {
        if (
            fieldNames.every((fieldName: keyof FilterInput): boolean => {
                const filterValue = filterInput?.[fieldName]?.eq ?? null;

                return filterValue === null;
            })
        ) {
            setResetTimestamp(Date.now());
        }
    }, [filterInput]);

    return (
        <DropdownInput
            addClasses="mx-1"
            defaultId={defaultOption?.id ?? null}
            elementId={elementId}
            handleChange={(
                prevOption: DropdownInputOption | null,
                option: DropdownInputOption | null,
            ): void => {
                const nextFilterInputs = fieldNames.reduce(
                    (
                        acc: Record<
                            keyof FilterInput,
                            TableStringFilterInput | undefined
                        >,
                        curr: keyof FilterInput,
                    ): Record<
                        keyof FilterInput,
                        TableStringFilterInput | undefined
                    > => {
                        // There's a conflict here between the typing on DropdownInputOption vs.
                        // TableStringFilterInput. There is probably a better way to type these to
                        // avoid using the `@ts-ignore` below, but for now this is working.
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        const fieldValue = option.value?.[curr] ?? null;

                        if (fieldValue) {
                            acc[curr] = {
                                eq: fieldValue,
                            };
                        } else {
                            acc[curr] = undefined;
                        }

                        return acc;
                    },
                    {} as Record<
                        keyof FilterInput,
                        TableStringFilterInput | undefined
                    >,
                );

                setFilterInput(
                    (prev: FilterInput): FilterInput => ({
                        ...prev,
                        ...nextFilterInputs,
                    }),
                );
            }}
            highlightWhenOpen={false}
            options={options}
            height={height}
            width={width}
            resetTimestamp={resetTimestamp}
        />
    );
}
